import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Save, Send, EyeOff, Sparkles, Image as ImageIcon, Plus, Trash2, Eye, X, Wand2, Linkedin, Copy, ChevronDown, Globe,
} from 'lucide-react';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../context/AdminAuthContext';
import {
  cmsGet, cmsCreate, cmsUpdate, cmsSetStatus, cmsAIWrite, cmsAIImage, cmsAISeo, cmsUploadImage, resolveImg,
  linkedinStatus, linkedinCaption, linkedinShare, linkedinHistory, linkedinOAuthStart, linkedinDisconnect,
} from '../lib/marketingApi';
import { BlogArticle, CaseStudyArticle } from '../components/ArticleView';

const URLSEG_TO_TYPE = { blogs: 'blog', 'case-studies': 'case_study' };
const BLOG_CATEGORIES = ['Operations', 'Multifamily', 'Energy Brokers', 'Payments', 'Sustainability', 'Technology', 'Product'];
const CS_SEGMENTS = ['Multifamily', 'Commercial & Industrial', 'Energy Brokers', 'Bill Validation'];

const parasToText = (arr) => (Array.isArray(arr) ? arr.join('\n\n') : '');
const textToParas = (t) => (t || '').split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
const linesToArr = (t) => (t || '').split('\n').map((s) => s.trim()).filter(Boolean);

function emptyBlog() {
  return { title: '', slug: '', category: 'Operations', excerpt: '', author: 'BillPortal Team', date: '', read_time: 5, image: '', body: [{ heading: '', paragraphs: [''] }], seo: { meta_title: '', meta_description: '' }, linkedin_post: '' };
}
function emptyCs() {
  return { title: '', slug: '', segment: 'Commercial & Industrial', excerpt: '', profile: '', image: '', metrics: [{ value: '', label: '' }], challenge: [], solution: [], approach: [], results: [], quote: { text: '', attribution: '' }, seo: { meta_title: '', meta_description: '' } };
}

// text-backed mirrors for array fields (edited as textareas)
function blogToForm(b) {
  return { ...emptyBlog(), ...b, body: (b.body && b.body.length ? b.body : [{ heading: '', paragraphs: [''] }]).map((s) => ({ heading: s.heading || '', text: parasToText(s.paragraphs) })), seo: { meta_title: '', meta_description: '', keywords: [], ...(b.seo || {}) } };
}
function csToForm(c) {
  return {
    ...emptyCs(), ...c,
    metrics: (c.metrics && c.metrics.length ? c.metrics : [{ value: '', label: '' }]),
    challengeText: parasToText(c.challenge), solutionText: parasToText(c.solution),
    approachText: parasToText(c.approach), resultsText: (c.results || []).join('\n'),
    quote: c.quote || { text: '', attribution: '' }, seo: { meta_title: '', meta_description: '', keywords: [], ...(c.seo || {}) },
  };
}

export default function ContentEditor() {
  const { type: urlType, id } = useParams();
  const type = URLSEG_TO_TYPE[urlType] || 'blog';
  const isCs = type === 'case_study';
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  const [form, setForm] = useState(null);
  const [status, setStatus] = useState('draft');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  // AI
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiInstr, setAiInstr] = useState('');
  const [aiTone, setAiTone] = useState('professional');
  const [aiBusy, setAiBusy] = useState(false);
  const [imgPrompt, setImgPrompt] = useState('');
  const [imgBusy, setImgBusy] = useState(false);
  const [seoBusy, setSeoBusy] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(null); // null | { share: bool|undefined }
  const [pubMenuOpen, setPubMenuOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const [liAuto, setLiAuto] = useState(false);

  useEffect(() => { linkedinStatus().then((s) => setLiAuto(!!s?.auto_post)).catch(() => {}); }, []);

  const genSeo = async () => {
    if (!form.title) { toast.error('Add a title first'); return; }
    setSeoBusy(true);
    try {
      const bodyText = isCs
        ? [form.excerpt, form.challengeText, form.solutionText, form.approachText, form.resultsText].filter(Boolean).join('\n')
        : (form.body || []).map((s) => `${s.heading || ''}\n${s.text || ''}`).join('\n');
      const d = await cmsAISeo({ kind: type, title: form.title, excerpt: form.excerpt, body_text: bodyText });
      up({ seo: { ...form.seo, ...d.seo } });
      toast.success('SEO meta generated');
    } catch (e) { toast.error('SEO generation failed'); }
    finally { setSeoBusy(false); }
  };

  useEffect(() => {
    if (isNew) { setForm(isCs ? csToForm(emptyCs()) : blogToForm(emptyBlog())); return; }
    (async () => {
      try {
        const d = await cmsGet(type, id);
        setStatus(d.item.status);
        setMeta(d.item);
        setForm(isCs ? csToForm(d.item) : blogToForm(d.item));
      } catch (e) { toast.error('Not found'); navigate(`/admin/content/${urlType}`); }
      finally { setLoading(false); }
    })();
  }, [id]); // eslint-disable-line

  const up = (patch) => setForm((f) => ({ ...f, ...patch }));

  const copyLinkedIn = async () => {
    try { await navigator.clipboard.writeText(form.linkedin_post || ''); toast.success('LinkedIn post copied to clipboard'); }
    catch (e) { toast.error('Copy failed — select and copy manually'); }
  };

  const buildPayload = useCallback(() => {
    if (isCs) {
      return {
        title: form.title, slug: form.slug, segment: form.segment, excerpt: form.excerpt,
        profile: form.profile, image: form.image,
        metrics: (form.metrics || []).filter((m) => m.value || m.label),
        challenge: textToParas(form.challengeText), solution: textToParas(form.solutionText),
        approach: textToParas(form.approachText), results: linesToArr(form.resultsText),
        quote: form.quote, seo: form.seo,
      };
    }
    return {
      title: form.title, slug: form.slug, category: form.category, excerpt: form.excerpt,
      author: form.author, date: form.date || undefined, read_time: Number(form.read_time) || 5,
      image: form.image, seo: form.seo, linkedin_post: form.linkedin_post || '',
      body: (form.body || []).map((s) => ({ heading: s.heading, paragraphs: textToParas(s.text) })).filter((s) => s.heading || s.paragraphs.length),
    };
  }, [form, isCs]);

  // Build a preview object matching public component shape
  const previewObj = useCallback(() => {
    if (isCs) {
      return {
        ...form, metrics: (form.metrics || []).filter((m) => m.value || m.label),
        challenge: textToParas(form.challengeText), solution: textToParas(form.solutionText),
        approach: textToParas(form.approachText), results: linesToArr(form.resultsText),
      };
    }
    return { ...form, readTime: form.read_time, body: (form.body || []).map((s) => ({ heading: s.heading, paragraphs: textToParas(s.text) })) };
  }, [form, isCs]);

  const save = async (publishAfter = false, shareLinkedin = undefined) => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      let itemId = id;
      if (isNew) {
        const d = await cmsCreate(type, buildPayload());
        itemId = d.item.id;
      } else {
        await cmsUpdate(type, id, buildPayload());
      }
      if (publishAfter) {
        await cmsSetStatus(type, itemId, 'published', shareLinkedin);
        setStatus('published');
        toast.success(shareLinkedin ? 'Published — live on the site & posting to LinkedIn' : 'Published — now live on the site');
      } else {
        toast.success('Saved as draft');
      }
      navigate(`/admin/content/${urlType}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const unpublish = async () => {
    setSaving(true);
    try { await cmsSetStatus(type, id, 'draft'); setStatus('draft'); toast.success('Unpublished'); }
    catch (e) { toast.error('Failed'); } finally { setSaving(false); }
  };

  const runAI = async () => {
    if (!aiTopic.trim()) { toast.error('Give the AI a topic'); return; }
    setAiBusy(true);
    try {
      const d = await cmsAIWrite({ kind: type, topic: aiTopic, instructions: aiInstr, tone: aiTone });
      const draft = d.draft;
      if (isCs) {
        setForm((f) => ({
          ...f, title: draft.title || f.title, segment: draft.segment || f.segment, excerpt: draft.excerpt || f.excerpt,
          profile: draft.profile || f.profile, metrics: (draft.metrics && draft.metrics.length ? draft.metrics : f.metrics),
          challengeText: parasToText(draft.challenge), solutionText: parasToText(draft.solution),
          approachText: parasToText(draft.approach), resultsText: (draft.results || []).join('\n'),
          quote: draft.quote || f.quote, seo: draft.seo || f.seo,
        }));
      } else {
        setForm((f) => ({
          ...f, title: draft.title || f.title, category: draft.category || f.category, excerpt: draft.excerpt || f.excerpt,
          read_time: draft.read_time || f.read_time, seo: draft.seo || f.seo,
          linkedin_post: draft.linkedin_post || f.linkedin_post,
          body: (draft.body && draft.body.length ? draft.body : []).map((s) => ({ heading: s.heading || '', text: parasToText(s.paragraphs) })),
        }));
      }
      toast.success('AI draft ready — review & edit before publishing');
      setAiOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'AI writing failed'); } finally { setAiBusy(false); }
  };

  const genImage = async () => {
    const p = imgPrompt.trim() || form.title;
    if (!p) { toast.error('Enter an image prompt or a title first'); return; }
    setImgBusy(true);
    try { const d = await cmsAIImage(p); up({ image: d.url }); toast.success('Cover image generated'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Image generation failed'); } finally { setImgBusy(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgBusy(true);
    try { const d = await cmsUploadImage(file); up({ image: d.url }); toast.success('Image uploaded'); }
    catch (e2) { toast.error('Upload failed'); } finally { setImgBusy(false); }
  };

  if (loading || !form) {
    return (<div className="min-h-screen bg-slate-50"><AdminHeader active="content" /><div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div></div>);
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="content-editor">
      <AdminHeader active="content" actions={(
        <div className="flex items-center gap-2">
          <button onClick={() => setPreview(true)} data-testid="content-preview-btn" className="inline-flex h-9 items-center gap-1.5 rounded-full border border-slate-200 px-3.5 text-sm font-medium text-slate-700 hover:bg-slate-50"><Eye className="h-4 w-4" /> Preview</button>
          {canEdit && <button onClick={() => save(false)} disabled={saving} data-testid="content-save-draft-btn" className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"><Save className="h-4 w-4" /> Save draft</button>}
          {canEdit && status !== 'published' && !isCs && (
            <div className="relative">
              <button onClick={() => setPubMenuOpen((o) => !o)} disabled={saving} data-testid="content-publish-btn" className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60"><Send className="h-4 w-4" /> Publish <ChevronDown className="h-4 w-4" /></button>
              {pubMenuOpen && (
                <>
                  <div className="fixed inset-0 z-[55]" onClick={() => setPubMenuOpen(false)} />
                  <div className="absolute right-0 z-[56] mt-1 w-64 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg" data-testid="publish-menu">
                    <button onClick={() => { setPubMenuOpen(false); setConfirmPublish({ share: false }); }} data-testid="publish-website-only" className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-slate-50">
                      <Globe className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                      <span><span className="block text-sm font-semibold text-slate-800">Website only</span><span className="block text-[11px] text-slate-500">Publish live on the site</span></span>
                    </button>
                    <button onClick={() => { setPubMenuOpen(false); setConfirmPublish({ share: true }); }} data-testid="publish-web-linkedin" className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-slate-50">
                      <Linkedin className="mt-0.5 h-4 w-4 shrink-0 text-[#0A66C2]" />
                      <span><span className="block text-sm font-semibold text-slate-800">Web + LinkedIn</span><span className="block text-[11px] text-slate-500">Publish and post to your LinkedIn page</span></span>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          {canEdit && status !== 'published' && isCs && <button onClick={() => setConfirmPublish({ share: undefined })} disabled={saving} data-testid="content-publish-btn" className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60"><Send className="h-4 w-4" /> Publish</button>}
          {canEdit && status === 'published' && !isNew && <button onClick={unpublish} disabled={saving} data-testid="content-unpublish-btn" className="inline-flex h-9 items-center gap-1.5 rounded-md bg-amber-500 px-3.5 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-60"><EyeOff className="h-4 w-4" /> Unpublish</button>}
        </div>
      )} />

      {confirmPublish && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="publish-confirm-modal" onClick={() => setConfirmPublish(null)}>
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0035A8]/10 text-[#0035A8]"><Send className="h-5 w-5" /></span>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Publish this {isCs ? 'case study' : 'article'}?</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                  {confirmPublish.share === true
                    ? 'This will go live on the website and be posted to your connected LinkedIn page.'
                    : confirmPublish.share === false
                      ? 'This will go live on the website only. It will NOT be posted to LinkedIn.'
                      : `Once you publish, this will go live on the website${liAuto ? ' and be automatically posted to your connected LinkedIn page' : ''}.`}
                  {' '}Are you sure you want to go ahead?
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setConfirmPublish(null)} data-testid="publish-confirm-cancel" className="inline-flex h-10 items-center rounded-md border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={async () => { const share = confirmPublish.share; setConfirmPublish(null); await save(true, share); }} disabled={saving} data-testid="publish-confirm-yes" className="inline-flex h-10 items-center gap-1.5 rounded-md bg-[#0035A8] px-4 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60"><Send className="h-4 w-4" /> Yes, publish</button>
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
        <button onClick={() => navigate(`/admin/content/${urlType}`)} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" /> Back to {isCs ? 'case studies' : 'blogs'}</button>

        <div className="mb-4 flex items-center gap-3">
          <h1 className="text-xl font-bold tracking-tight text-slate-900">{isNew ? `New ${isCs ? 'case study' : 'article'}` : 'Edit content'}</h1>
          <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{status === 'published' ? 'Published' : 'Draft'}</span>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* MAIN FORM */}
          <div className="space-y-5 lg:col-span-8">
            <Card>
              <div className="space-y-4 p-5">
                <Field label="Title"><input data-testid="field-title" value={form.title} onChange={(e) => up({ title: e.target.value })} className="inp" placeholder="A clear, specific headline" /></Field>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label={isCs ? 'Segment' : 'Category'}>
                    <select data-testid="field-category" value={isCs ? form.segment : form.category} onChange={(e) => up(isCs ? { segment: e.target.value } : { category: e.target.value })} className="inp">
                      {(isCs ? CS_SEGMENTS : BLOG_CATEGORIES).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Slug (URL)"><input data-testid="field-slug" value={form.slug} onChange={(e) => up({ slug: e.target.value })} className="inp" placeholder="auto-generated from title" /></Field>
                </div>
                <Field label="Excerpt / summary"><textarea data-testid="field-excerpt" rows={2} value={form.excerpt} onChange={(e) => up({ excerpt: e.target.value })} className="inp resize-none" placeholder="1–2 sentence summary shown on cards" /></Field>
                {isCs ? (
                  <Field label="Client profile"><input value={form.profile} onChange={(e) => up({ profile: e.target.value })} className="inp" placeholder="e.g. 12 operators · 14 U.S. states" /></Field>
                ) : (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Field label="Author"><input value={form.author} onChange={(e) => up({ author: e.target.value })} className="inp" /></Field>
                    <Field label="Date"><input type="date" value={form.date || ''} onChange={(e) => up({ date: e.target.value })} className="inp" /></Field>
                    <Field label="Read time (min)"><input type="number" min="1" value={form.read_time} onChange={(e) => up({ read_time: e.target.value })} className="inp" /></Field>
                  </div>
                )}
              </div>
            </Card>

            {/* Body */}
            {isCs ? (
              <Card>
                <CardTitle>Case study content</CardTitle>
                <div className="space-y-4 p-5">
                  <Field label="The challenge (blank line separates paragraphs)"><textarea rows={4} value={form.challengeText} onChange={(e) => up({ challengeText: e.target.value })} className="inp resize-y" /></Field>
                  <Field label="The solution"><textarea rows={4} value={form.solutionText} onChange={(e) => up({ solutionText: e.target.value })} className="inp resize-y" /></Field>
                  <Field label="The approach"><textarea rows={4} value={form.approachText} onChange={(e) => up({ approachText: e.target.value })} className="inp resize-y" /></Field>
                  <Field label="The results (one outcome per line)"><textarea rows={4} value={form.resultsText} onChange={(e) => up({ resultsText: e.target.value })} className="inp resize-y" /></Field>
                  <div>
                    <div className="mb-1.5 flex items-center justify-between"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Headline metrics</span>
                      <button onClick={() => up({ metrics: [...form.metrics, { value: '', label: '' }] })} className="inline-flex items-center gap-1 text-xs font-semibold text-[#0035A8]"><Plus className="h-3.5 w-3.5" /> Add metric</button></div>
                    <div className="space-y-2">
                      {form.metrics.map((m, i) => (
                        <div key={i} className="flex gap-2">
                          <input value={m.value} onChange={(e) => { const a = [...form.metrics]; a[i] = { ...a[i], value: e.target.value }; up({ metrics: a }); }} placeholder="$30M+" className="inp w-32" />
                          <input value={m.label} onChange={(e) => { const a = [...form.metrics]; a[i] = { ...a[i], label: e.target.value }; up({ metrics: a }); }} placeholder="Label" className="inp flex-1" />
                          <button onClick={() => up({ metrics: form.metrics.filter((_, j) => j !== i) })} className="rounded p-2 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Quote"><textarea rows={2} value={form.quote.text} onChange={(e) => up({ quote: { ...form.quote, text: e.target.value } })} className="inp resize-none" /></Field>
                    <Field label="Quote attribution"><input value={form.quote.attribution} onChange={(e) => up({ quote: { ...form.quote, attribution: e.target.value } })} className="inp" /></Field>
                  </div>
                </div>
              </Card>
            ) : (
              <Card>
                <CardTitle right={<button onClick={() => up({ body: [...form.body, { heading: '', text: '' }] })} data-testid="add-section-btn" className="inline-flex items-center gap-1 text-xs font-semibold text-[#0035A8]"><Plus className="h-3.5 w-3.5" /> Add section</button>}>Article body</CardTitle>
                <div className="space-y-4 p-5">
                  {form.body.map((s, i) => (
                    <div key={i} className="rounded-lg border border-slate-200 p-3" data-testid={`body-section-${i}`}>
                      <div className="mb-2 flex items-center gap-2">
                        <input value={s.heading} onChange={(e) => { const a = [...form.body]; a[i] = { ...a[i], heading: e.target.value }; up({ body: a }); }} placeholder="Section heading" className="inp flex-1 font-semibold" />
                        <button onClick={() => up({ body: form.body.filter((_, j) => j !== i) })} className="rounded p-2 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                      <textarea rows={4} value={s.text} onChange={(e) => { const a = [...form.body]; a[i] = { ...a[i], text: e.target.value }; up({ body: a }); }} placeholder="Paragraphs (blank line separates paragraphs)" className="inp resize-y" />
                    </div>
                  ))}
                  {form.body.length === 0 && <p className="text-sm text-slate-400">No sections yet — add one or use “Write with AI”.</p>}
                </div>
              </Card>
            )}

            <Card>
              <CardTitle>Audit trail</CardTitle>
              <div className="space-y-3 p-5 text-sm" data-testid="content-audit">
                {isNew ? <p className="text-slate-400">Not created yet — save to start the audit trail.</p> : [
                  { label: 'Created by', who: meta?.created_by, when: meta?.created_at, empty: '—' },
                  { label: 'Last updated by', who: meta?.updated_by, when: meta?.updated_at, empty: '—' },
                  { label: 'Published by', who: meta?.published_by, when: meta?.published_at, empty: 'Not published yet' },
                ].map((r) => (
                  <div key={r.label} className="flex flex-col">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{r.label}</span>
                    {r.who ? (
                      <span className="text-slate-800" data-testid={`audit-${r.label.toLowerCase().replace(/ /g, '-')}`}>{r.who}{r.when ? <span className="text-slate-400"> · {fmtTime(r.when)}</span> : null}</span>
                    ) : <span className="text-slate-400">{r.empty}</span>}
                  </div>
                ))}
              </div>
            </Card>

            <Card>
              <CardTitle>SEO</CardTitle>
              <div className="space-y-4 p-5">
                <button onClick={genSeo} disabled={seoBusy || !canEdit} data-testid="ai-seo-btn" className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md border border-[#0035A8] text-sm font-semibold text-[#0035A8] hover:bg-[#0035A8]/5 disabled:opacity-60">{seoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate SEO with AI</button>
                <Field label="Meta title"><input data-testid="field-meta-title" value={form.seo?.meta_title || ''} onChange={(e) => up({ seo: { ...form.seo, meta_title: e.target.value } })} className="inp" /></Field>
                <Field label="Meta description"><textarea data-testid="field-meta-description" rows={2} value={form.seo?.meta_description || ''} onChange={(e) => up({ seo: { ...form.seo, meta_description: e.target.value } })} className="inp resize-none" /></Field>
                <Field label="Keywords (comma separated)"><input data-testid="field-keywords" value={(form.seo?.keywords || []).join(', ')} onChange={(e) => up({ seo: { ...form.seo, keywords: e.target.value.split(',').map((k) => k.trim()).filter(Boolean) } })} className="inp" placeholder="utility bill management, AP automation, ..." /></Field>
              </div>
            </Card>

            {!isCs && (
              <Card>
                <CardTitle icon={Linkedin}>LinkedIn post (for review)</CardTitle>
                <div className="space-y-3 p-5">
                  <p className="text-xs leading-relaxed text-slate-500">Auto-drafted, ready-to-post LinkedIn copy (hashtags included). The public article link is appended automatically when you publish — then just copy &amp; paste into LinkedIn.</p>
                  <textarea data-testid="field-linkedin-post" rows={8} value={form.linkedin_post || ''} onChange={(e) => up({ linkedin_post: e.target.value })} className="inp resize-y" placeholder="LinkedIn post copy will appear here once the article is generated with AI…" />
                  {status === 'published' && form.linkedin_post && !form.linkedin_post.includes('/blog/') && (
                    <p className="text-[11px] text-amber-600">Tip: the article link is added on publish. Re-publish or edit to append it if missing.</p>
                  )}
                  <button type="button" onClick={copyLinkedIn} disabled={!form.linkedin_post} data-testid="linkedin-copy-btn" className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-md bg-[#0A66C2] text-sm font-semibold text-white hover:bg-[#08528f] disabled:opacity-50">
                    <Copy className="h-4 w-4" /> Copy for LinkedIn
                  </button>
                </div>
              </Card>
            )}
          </div>

          {/* SIDEBAR */}
          <aside className="space-y-5 lg:col-span-4">
            {canEdit && (
              <Card>
                <CardTitle icon={Sparkles}>Write with AI</CardTitle>
                <div className="space-y-3 p-5">
                  <p className="text-xs leading-relaxed text-slate-500">Describe what to write. AI drafts it in BillPortal's voice — you review and edit before publishing.</p>
                  <button onClick={() => setAiOpen(true)} data-testid="ai-write-open-btn" className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md bg-[#0035A8] text-sm font-semibold text-white hover:bg-[#002B87]"><Wand2 className="h-4 w-4" /> Generate draft</button>
                </div>
              </Card>
            )}

            <Card>
              <CardTitle icon={ImageIcon}>Cover image</CardTitle>
              <div className="space-y-3 p-5">
                {form.image ? (
                  <div className="relative overflow-hidden rounded-lg border border-slate-200">
                    <img src={resolveImg(form.image)} alt="cover" className="h-40 w-full object-cover" />
                    <button onClick={() => up({ image: '' })} className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white hover:bg-black"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ) : <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-slate-200 text-xs text-slate-400">No cover image</div>}
                {canEdit && (
                  <>
                    <input value={imgPrompt} onChange={(e) => setImgPrompt(e.target.value)} placeholder="AI image prompt (defaults to title)" className="inp" data-testid="img-prompt" />
                    <button onClick={genImage} disabled={imgBusy} data-testid="ai-image-btn" className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md border border-[#0035A8] text-sm font-semibold text-[#0035A8] hover:bg-[#0035A8]/5 disabled:opacity-60">{imgBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate with AI</button>
                    <div className="flex items-center gap-2">
                      <input value={form.image?.startsWith('/') ? '' : form.image} onChange={(e) => up({ image: e.target.value })} placeholder="or paste image URL" className="inp flex-1" />
                    </div>
                    <label className="inline-flex h-9 w-full cursor-pointer items-center justify-center gap-1.5 rounded-md border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">
                      Upload image<input type="file" accept="image/*" onChange={onUpload} className="hidden" />
                    </label>
                  </>
                )}
              </div>
            </Card>

            {!isNew && <LinkedInCard type={type} id={id} status={status} form={form} canEdit={canEdit} />}
          </aside>
        </div>
      </div>

      {/* AI modal */}
      {aiOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="ai-modal">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between"><h3 className="flex items-center gap-2 text-lg font-bold text-slate-900"><Sparkles className="h-5 w-5 text-[#0035A8]" /> Write with AI</h3><button onClick={() => setAiOpen(false)} className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button></div>
            <div className="space-y-3">
              <Field label="Topic / title idea"><input data-testid="ai-topic" value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} placeholder={isCs ? 'e.g. Energy broker scaling with automated data' : 'e.g. Reducing late fees with automated payments'} className="inp" /></Field>
              <Field label="Extra instructions (optional)"><textarea data-testid="ai-instructions" rows={3} value={aiInstr} onChange={(e) => setAiInstr(e.target.value)} placeholder="Angle, key points, audience, must-mention capabilities…" className="inp resize-none" /></Field>
              <Field label="Tone"><select value={aiTone} onChange={(e) => setAiTone(e.target.value)} className="inp"><option value="professional">Professional</option><option value="conversational">Conversational</option><option value="authoritative">Authoritative</option><option value="friendly">Friendly</option></select></Field>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setAiOpen(false)} className="rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={runAI} disabled={aiBusy} data-testid="ai-generate-btn" className="inline-flex items-center gap-1.5 rounded-md bg-[#0035A8] px-4 py-2 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">{aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} {aiBusy ? 'Writing…' : 'Generate'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60" data-testid="preview-modal">
          <div className="mx-auto my-8 max-w-4xl overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3">
              <span className="text-sm font-semibold text-slate-700">Preview — how it will look on the site</span>
              <button onClick={() => setPreview(false)} data-testid="preview-close-btn" className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="bp-site">
              <div className="bg-white text-slate-900">
                {isCs ? <CaseStudyArticle cs={previewObj()} /> : <BlogArticle post={previewObj()} />}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`.inp{width:100%;border:1px solid #e2e8f0;border-radius:0.375rem;padding:0.5rem 0.75rem;font-size:0.875rem;color:#0f172a;outline:none}.inp:focus{border-color:#0035A8}`}</style>
    </div>
  );
}

function Card({ children }) { return <div className="rounded-lg border border-slate-200 bg-white">{children}</div>; }function CardTitle({ icon: Icon, children, right }) {
  return (<div className="flex items-center justify-between border-b border-slate-100 px-5 py-3"><div className="flex items-center gap-2 text-sm font-semibold text-slate-800">{Icon && <Icon className="h-4 w-4 text-[#0035A8]" />} {children}</div>{right}</div>);
}
function Field({ label, children }) {
  return (<label className="block"><span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>{children}</label>);
}

function fmtTime(iso) {
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); } catch (e) { return iso; }
}

function LinkedInCard({ type, id, status, form, canEdit }) {
  const [liStatus, setLiStatus] = useState(null);
  const [caption, setCaption] = useState('');
  const [extra, setExtra] = useState('');
  const [genBusy, setGenBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [connBusy, setConnBusy] = useState(false);

  const loadHistory = useCallback(async () => {
    try { const d = await linkedinHistory(type, id); setHistory(d.items || []); } catch (e) { /* noop */ }
  }, [type, id]);

  useEffect(() => {
    linkedinStatus().then(setLiStatus).catch(() => setLiStatus(null));
    loadHistory();
  }, [loadHistory]);

  const genCaption = async () => {
    setGenBusy(true);
    try { const d = await linkedinCaption(type, id, extra); setCaption(d.caption || ''); toast.success('Caption drafted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Caption failed'); } finally { setGenBusy(false); }
  };

  const share = async () => {
    setShareBusy(true);
    try {
      const d = await linkedinShare(type, id, { caption: caption || undefined, image_url: form.image || undefined, extra });
      const mode = d.post?.mode;
      if (d.post?.status === 'posted') toast.success('Posted to LinkedIn 🎉');
      else if (d.post?.status === 'simulated') toast.success('Simulated post logged (LinkedIn not connected yet)');
      else toast.error(d.post?.error || 'Share failed');
      if (!caption && d.post?.caption) setCaption(d.post.caption);
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || 'Share failed'); } finally { setShareBusy(false); }
  };

  const live = liStatus?.live;

  const connect = async () => {
    setConnBusy(true);
    try { const d = await linkedinOAuthStart(); window.location.href = d.url; }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not start LinkedIn connect'); setConnBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm('Disconnect the BillPortal LinkedIn page?')) return;
    setConnBusy(true);
    try { await linkedinDisconnect(); const s = await linkedinStatus(); setLiStatus(s); toast.success('Disconnected'); }
    catch (e) { toast.error('Failed'); } finally { setConnBusy(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="linkedin-card">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Linkedin className="h-4 w-4 text-[#0A66C2]" /> Share to LinkedIn</div>
        {liStatus && (
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${live ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
            {live ? 'Connected' : 'Simulation'}
          </span>
        )}
      </div>
      <div className="space-y-3 p-5">
        {status !== 'published' && <p className="rounded-md bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-700" data-testid="li-draft-note">This {type === 'case_study' ? 'case study' : 'article'} is a <b>draft</b>, so it can’t be posted to LinkedIn yet. Publishing it will {liStatus?.auto_post ? 'auto-post to LinkedIn' : 'not auto-post (auto-post is off)'}. You can still draft a caption below.</p>}
        {live && liStatus?.org_name && <p className="rounded-md bg-emerald-50 px-3 py-2 text-[11px] text-emerald-700">Connected to <b>{liStatus.org_name}</b>. Posts go live to your page.</p>}
        {!live && liStatus && canEdit && (
          liStatus.app_configured ? (
            <div className="rounded-md bg-amber-50 px-3 py-3 text-[11px] leading-relaxed text-amber-800">
              Not connected yet — running in simulation. Connect the BillPortal page to post live.
              <button onClick={connect} disabled={connBusy} data-testid="li-connect-btn" className="mt-2 inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-md bg-[#0A66C2] text-sm font-semibold text-white hover:bg-[#08528f] disabled:opacity-60">{connBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Linkedin className="h-4 w-4" />} Connect LinkedIn</button>
            </div>
          ) : (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-700">Simulation mode. Add the LinkedIn app Client ID/Secret + redirect URI in <code>.env</code> to enable Connect.</p>
          )
        )}
        {canEdit && (
          <>
            <Field label="Extra guidance for AI (optional)"><input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="e.g. target CFOs, emphasise ROI" className="inp" data-testid="li-extra" /></Field>
            <button onClick={genCaption} disabled={genBusy} data-testid="li-caption-btn" className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-md border border-[#0A66C2] text-sm font-semibold text-[#0A66C2] hover:bg-[#0A66C2]/5 disabled:opacity-60">{genBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Draft caption with AI</button>
            <textarea rows={5} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="AI caption will appear here — edit freely, or leave blank to auto-generate on post." className="inp resize-y" data-testid="li-caption" />
            <button onClick={share} disabled={shareBusy || status !== 'published'} data-testid="li-share-btn" title={status !== 'published' ? 'Publish this content first' : ''} className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md bg-[#0A66C2] text-sm font-semibold text-white hover:bg-[#08528f] disabled:cursor-not-allowed disabled:opacity-50">{shareBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Linkedin className="h-4 w-4" />} {status !== 'published' ? 'Publish to enable posting' : (live ? 'Post to LinkedIn' : 'Simulate post')}</button>
            {live && <button onClick={disconnect} disabled={connBusy} data-testid="li-disconnect-btn" className="w-full text-center text-[11px] font-medium text-slate-400 hover:text-rose-600">Disconnect LinkedIn</button>}
          </>
        )}
        {history.length > 0 && (
          <div className="border-t border-slate-100 pt-3" data-testid="li-history">
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">LinkedIn post history ({history.length})</div>
            <div className="space-y-2">
              {history.slice(0, 50).map((h) => {
                const auto = h.trigger === 'auto_publish' || h.actor === 'auto';
                return (
                  <div key={h.id} className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2" data-testid="li-history-item">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className={`font-semibold ${h.status === 'posted' ? 'text-emerald-600' : h.status === 'failed' ? 'text-rose-600' : 'text-amber-600'}`}>{h.status}{h.mode === 'simulation' ? ' · simulated' : h.mode === 'live' ? ' · live' : ''}</span>
                      <span className="text-slate-400">{fmtTime(h.created_at)}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${auto ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700'}`}>
                        {auto ? 'Auto-posted on publish' : 'Manual post'}
                      </span>
                      <span className="text-[10px] text-slate-400">{auto ? 'AI-generated caption' : `by ${h.actor || 'unknown'}`}</span>
                    </div>
                    <p className="mt-1.5 whitespace-pre-wrap text-xs leading-relaxed text-slate-700" data-testid="li-history-caption">{h.caption || <span className="italic text-slate-400">(no caption recorded)</span>}</p>
                    {h.error && <p className="mt-1 text-[11px] text-rose-600">Error: {h.error}</p>}
                    {h.post_url && <a href={h.post_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-[11px] font-medium text-[#0A66C2] hover:underline">View on LinkedIn ↗</a>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
